/**
 * Runtime conference config from the backend — single source of truth.
 *
 * The join window (how many minutes before start the "Подключиться" button and
 * the Zoom-room entry open) comes from the backend setting
 * ``CONFERENCE_JOIN_WINDOW_MINUTES`` (backend .env) via
 * ``GET /api/conferences/config/``. ``loadConferenceConfig()`` is awaited once at
 * app boot (main.ts) BEFORE the app mounts, so ``joinWindowMinutes()`` returns
 * the loaded value everywhere without per-component reactivity.
 *
 * This is runtime config: change it in the backend .env + restart the backend —
 * NO frontend rebuild required. Default 15 is a fail-safe if the call fails.
 */
let joinWindowMinutesValue = 15;

export function joinWindowMinutes(): number {
    return joinWindowMinutesValue;
}

/**
 * Окно подачи спора — сколько часов после сессии доступна жалоба. Бэкенд:
 * ``COMPLAINT_DEADLINE_HOURS`` (.env, дефолт 24). Используется FE-гейтом кнопки
 * «Открыть спор» в карточке календаря, чтобы не показывать её после истечения
 * окна (бэкенд остаётся авторитетным и отвергает просроченные жалобы 400).
 * Подхватывается из ``/conferences/config/`` (``complaint_window_hours``), если
 * бэкенд начнёт его отдавать; иначе остаётся дефолт 24.
 */
let complaintWindowHoursValue = 24;

export function complaintWindowHours(): number {
    return complaintWindowHoursValue;
}

/**
 * Грейс ПОСЛЕ окончания сессии (#5): сколько ещё минут «Подключиться» и плашка
 * «идёт сейчас» остаются активны после end_time, прежде чем погаснуть. Мы не
 * выключаем сессию сами в момент конца — даём досидеть/дообсудить. Бэкенд:
 * ``CONFERENCE_PLANNED_END_GRACE_MIN`` (.env, дефолт 20) — то же значение, по
 * которому BE держит комнату живой (force-close = planned_end + грейс), поэтому
 * FE-кнопка и реальная доступность Zoom-комнаты совпадают (нет «кнопка есть, а
 * токен не выдаётся»). Подхватывается из ``/conferences/config/``
 * (``session_end_grace_minutes``); иначе дефолт 20.
 */
let sessionEndGraceMinutesValue = 20;

export function sessionEndGraceMinutes(): number {
    return sessionEndGraceMinutesValue;
}

/**
 * #276 (эпик conference-gates-hardening): режим enforcement join-гейтов.
 * false (observe — дефолт бэка на старте): авто-статусы конференции
 * (no_show_*, late_canceled, …) НЕ блокируют вход — BE выдаёт токен и лишь
 * логирует «сработал бы», а FE-поверхности (карточка календаря, ZoomCallPage)
 * обязаны следовать тому же правилу и не прятать вход сами. «Факты»
 * (canceled_by_*, refunded, спор) блокируют всегда. FE-дефолт true —
 * КОНСЕРВАТИВНЫЙ: до загрузки конфига ведём себя строже сервера (поведение
 * #269); строже — безопасно, слабее — нет.
 */
let joinEnforcementValue = true;

export function joinEnforcement(): boolean {
    return joinEnforcementValue;
}

/** Russian plural form for ``n`` (1 минута / 2 минуты / 5 минут). */
function pluralRu(n: number, one: string, few: string, many: string): string {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
    return many;
}

/**
 * Human-readable join window phrase, e.g. ``за 15 минут`` / ``за 2 часа`` /
 * ``за 3 дня``. Single source of truth so the "Подключиться" hints across the
 * calendar card, event page, mentor page and Zoom lobby always match the
 * configured ``CONFERENCE_JOIN_WINDOW_MINUTES`` (and stay identical for
 * consultations and group events). Compose with `` до начала`` at the call site.
 */
export function joinWindowLabel(): string {
    const minutes = joinWindowMinutesValue;
    if (minutes % (60 * 24) === 0) {
        const days = minutes / (60 * 24);
        return `за ${days} ${pluralRu(days, 'день', 'дня', 'дней')}`;
    }
    if (minutes % 60 === 0) {
        const hours = minutes / 60;
        return `за ${hours} ${pluralRu(hours, 'час', 'часа', 'часов')}`;
    }
    return `за ${minutes} ${pluralRu(minutes, 'минуту', 'минуты', 'минут')}`;
}

export async function loadConferenceConfig(): Promise<void> {
    try {
        // Lazy import: keep this module a leaf for the many components that only
        // need joinWindowMinutes(). A static httpClient import would pull in the
        // router/store graph and break component specs that partially mock vuex.
        const { default: api } = await import('@/services/httpClient');
        const { data } = await api.get('/conferences/config/');
        const parsed = Number(data?.join_window_minutes);
        if (Number.isFinite(parsed) && parsed > 0) {
            joinWindowMinutesValue = Math.floor(parsed);
        }
        const parsedComplaint = Number(data?.complaint_window_hours);
        if (Number.isFinite(parsedComplaint) && parsedComplaint > 0) {
            complaintWindowHoursValue = Math.floor(parsedComplaint);
        }
        // #5: грейс после конца сессии (>= 0; 0 = гасить ровно в end_time).
        const parsedEndGrace = Number(data?.session_end_grace_minutes);
        if (Number.isFinite(parsedEndGrace) && parsedEndGrace >= 0) {
            sessionEndGraceMinutesValue = Math.floor(parsedEndGrace);
        }
        // #276: observe-режим join-гейтов (см. joinEnforcement выше).
        if (typeof data?.join_enforcement === 'boolean') {
            joinEnforcementValue = data.join_enforcement;
        }
    } catch {
        // Keep the default (15) — joining stays gated to the standard window.
    }
}
